import React from 'react';
import { useLocalization } from '../../../../../config/localization';
import Icon, { ICONS } from '../../../../ui/icons';
import type { BaseSectionProps } from './index';

export default function EnhanceFieldMappingsSection({
  formData,
  onFormDataChange,
  language
}: BaseSectionProps) {
  const t = useLocalization(language);
  const mappings = formData.settings?.enhance_field_mappings || [];

  const updateMappings = (newMappings: { source: string; target: string }[]) => {
    onFormDataChange('settings', {
      ...(formData.settings || {}),
      enhance_field_mappings: newMappings.length > 0 ? newMappings : undefined
    });
  };

  return (
    <div className="space-y-6">
      <section>
        <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
          <Icon name={ICONS.SPARKLES} size={20} className="text-purple-400" />
          {t.admin?.autoPromptEnhance?.mappingsLabel || 'Enhance Field Mappings'}
        </h3>
        <p className="text-sm text-gray-400 mb-6">
          {t.admin?.autoPromptEnhance?.mappingsDescription || 'Configure how enhance API output fields map to generation input fields'}
        </p>

        <div className="space-y-3">
          {mappings.map((mapping, idx) => (
            <div key={idx} className="flex items-center gap-3">
              <input
                type="text"
                value={mapping.source}
                onChange={(e) => {
                  const updated = [...mappings];
                  updated[idx] = { ...updated[idx], source: e.target.value };
                  updateMappings(updated);
                }}
                placeholder={t.admin?.autoPromptEnhance?.sourceField || 'Source field'}
                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
              />
              <span className="text-gray-500 text-sm flex-shrink-0">&rarr;</span>
              <input
                type="text"
                value={mapping.target}
                onChange={(e) => {
                  const updated = [...mappings];
                  updated[idx] = { ...updated[idx], target: e.target.value };
                  updateMappings(updated);
                }}
                placeholder={t.admin?.autoPromptEnhance?.targetField || 'Target field'}
                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
              />
              <button
                type="button"
                onClick={() => updateMappings(mappings.filter((_, i) => i !== idx))}
                className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-gray-800 rounded transition-colors flex-shrink-0"
              >
                <Icon name={ICONS.TRASH} size={16} />
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={() => updateMappings([...mappings, { source: '', target: '' }])}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-blue-400 hover:text-blue-300 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <Icon name={ICONS.PLUS} size={14} />
            {t.admin?.autoPromptEnhance?.addMapping || 'Add mapping'}
          </button>
        </div>

        <p className="mt-4 text-xs text-gray-600">
          {t.admin?.autoPromptEnhance?.defaultHint || 'If empty, defaults: text \u2192 prompt, is_nsfw \u2192 is_nsfw'}
        </p>
      </section>
    </div>
  );
}
